/**
 * Event Product Form
 *
 * イベント商品専用のカスタムエレメント
 * product-formを継承し、カート追加前に参加者情報のバリデーションを実行
 *
 * レンタル商品連携:
 *   優先: data-rental-variant-id（新 metafield events.rental_variant 由来）
 *   旧:   data-rental-type + rentalConfig hardcode（フォールバック / Phase F で廃止予定）
 */

if (!customElements.get('event-product-form')) {
  customElements.define(
    'event-product-form',
    class EventProductForm extends customElements.get('product-form') {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.eventDate = this.dataset.eventDate; // イベント開催日（講習商品は必須）
        this.rentalType = this.dataset.rentalType || ''; // 旧：レンタル品タイプ

        // 新方式：metafield 由来のレンタル variant 直接指定（推奨）
        this.rentalVariantId = this.dataset.rentalVariantId || '';
        this.rentalHandle = this.dataset.rentalHandle || '';
        this.rentalLabel = this.dataset.rentalLabel || '';
        this.rentalPrice = parseInt(this.dataset.rentalPrice, 10) || 0;

        // 旧方式：rentalType によるルックアップ
        this.rentalConfig = {
          'ヘルメット': {
            handle: '講習用ヘルメットレンタル',
            label: 'ヘルメット',
            price: 500
          },
          'PPE': {
            handle: '講習用ヘルメットレンタル-コピー',
            label: 'PPE（個人装備）レンタル',
            price: 2500
          }
        };

        // rental オブジェクト構築：新方式優先、なければフォールバック
        if (this.rentalVariantId) {
          this.rental = {
            variantId: this.rentalVariantId,
            handle: this.rentalHandle,
            label: this.rentalLabel,
            price: this.rentalPrice,
            source: 'metafield'
          };
        } else if (this.rentalType && this.rentalConfig[this.rentalType]) {
          this.rental = Object.assign({}, this.rentalConfig[this.rentalType], {
            variantId: '',
            source: 'fallback'
          });
        } else {
          this.rental = null;
        }
      }

      /**
       * フォーム送信ハンドラーをオーバーライド
       * バリデーション成功時のみ親クラスの処理を実行
       */
      onSubmitHandler(evt) {
        evt.preventDefault();

        // ボタンが無効な場合は何もしない
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // ★ 整合性ガード（数量と参加者数の desync 防止 / fail-closed）
        //   送信直前に参加者欄を数量へ強制再同期し、
        //   「数量 == 送信される参加者数」かつ「レンタル必要数 <= 数量」でなければ送信を中止する。
        if (!this.enforceParticipantIntegrity()) {
          return;
        }

        // イベント商品用のバリデーション
        if (!this.validateEventParticipants()) {
          // バリデーション失敗時はカート追加を阻止
          return;
        }

        // レンタル品が必要かチェック
        const rentalNeeded = this.getRentalCount() > 0;

        if (rentalNeeded) {
          // レンタル品がある場合：メイン商品を自前でカートに追加し、完了後にレンタル品を追加
          this.addMainAndRental(evt);
        } else {
          // レンタル品不要：親クラスの処理をそのまま実行
          super.onSubmitHandler(evt);
        }
      }

      /**
       * 数量と参加者情報の整合性を強制する（desync 防止の最終防衛線 / fail-closed）
       * - 参加者欄を現在の数量へ強制再同期（残留欄を物理排除）
       * - 数量<1 / 数量≠送信参加者数 / レンタル必要数>数量 のいずれかなら送信中止
       * @returns {boolean} 整合していれば true、問題があれば false（送信中止）
       */
      enforceParticipantIntegrity() {
        const validator = window.EventProductValidation && window.EventProductValidation[this.sectionId];
        if (!validator || !validator.getQuantity) {
          // バリデータ未検出時は安全側に倒し、通常処理（親クラス）に委ねる
          console.warn('[event-form] integrity validator not found for section', this.sectionId);
          return true;
        }

        try {
          const qty = validator.getQuantity();

          // 1. 人数そのものの妥当性
          if (qty < 1) {
            if (validator.showError) validator.showError('お申込み人数（数量）を1以上で選択してください。');
            return false;
          }

          // 2. 在庫（定員）を超えていないか（2026-08-05 追加）
          //    数量欄の変更を経由せずに超過した状態（別タブ・戻る操作・在庫変動）を最後に捕まえる。
          const maxQ = validator.getMaxAllowedQty ? validator.getMaxAllowedQty() : Infinity;
          if (qty > maxQ) {
            console.error('[event-form] integrity guard blocked submit (over stock)', { qty, maxQ });
            if (validator.showError) {
              validator.showError(maxQ > 0
                ? `この講習にお申込みいただけるのは残り${maxQ}名です。お手数ですが人数を${maxQ}名以下に変更してください。`
                : 'この講習はこれ以上お申込みいただけません。カートの内容をご確認ください。');
            }
            return false;
          }

          // 3. 数量と「実際に送信される参加者情報の数」が一致しているか
          //    ※ 再同期する前に数える（先に再同期すると必ず一致してしまい検出できない）
          const participantCount = validator.countParticipantPayload
            ? validator.countParticipantPayload()
            : qty;
          if (participantCount !== qty) {
            console.error('[event-form] integrity guard blocked submit (desync)', { qty, participantCount });
            if (validator.syncParticipantFields) validator.syncParticipantFields();
            if (validator.showError) {
              validator.showError('お申込み人数と入力された参加者情報の数が一致しません。お手数ですが、お申込み人数分の「数量」を選択して入力し直してください。');
            }
            return false;
          }

          // 4. レンタル希望数が人数を超えていないか
          const rentalNeeded = validator.countRentalNeeded ? validator.countRentalNeeded() : 0;
          if (rentalNeeded > qty) {
            console.error('[event-form] integrity guard blocked submit (rental)', { qty, rentalNeeded });
            if (validator.showError) {
              validator.showError('レンタルをご希望の人数が、お申込み人数を超えています。ご確認ください。');
            }
            return false;
          }
          return true;
        } catch (e) {
          // 例外時は fail-closed（送信させない）
          console.error('[event-form] integrity guard error (fail-closed):', e);
          if (validator.showError) {
            validator.showError('お申込み内容の確認中にエラーが発生しました。ページを再読み込みしてもう一度お試しください。');
          }
          return false;
        }
      }

      /**
       * レンタル必要数を取得
       */
      getRentalCount() {
        if (!this.rental) return 0;
        if (window.EventProductValidation && window.EventProductValidation[this.sectionId]) {
          return window.EventProductValidation[this.sectionId].countRentalNeeded();
        }
        return 0;
      }

      /**
       * ローディングオーバーレイを表示
       */
      showLoadingOverlay() {
        let overlay = document.getElementById('event-loading-overlay');
        if (!overlay) {
          overlay = document.createElement('div');
          overlay.id = 'event-loading-overlay';
          overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:9999;display:flex;align-items:center;justify-content:center;flex-direction:column;';
          overlay.innerHTML = '<div style="width:48px;height:48px;border:4px solid #fff;border-top-color:transparent;border-radius:50%;animation:event-spin 0.8s linear infinite;"></div><p style="color:#fff;margin-top:16px;font-size:16px;">カートに追加しています...</p><style>@keyframes event-spin{to{transform:rotate(360deg)}}</style>';
          document.body.appendChild(overlay);
        }
        overlay.style.display = 'flex';
      }

      /**
       * ローディングオーバーレイを非表示
       */
      hideLoadingOverlay() {
        const overlay = document.getElementById('event-loading-overlay');
        if (overlay) overlay.style.display = 'none';
      }

      /**
       * メイン商品とレンタル品を順番にカートに追加
       */
      async addMainAndRental(evt) {
        const rental = this.rental;
        const eventDate = this.eventDate;
        const rentalCount = this.getRentalCount();

        // ボタンをローディング状態に
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.showLoadingOverlay();

        try {
          // 1. メイン商品をカートに追加
          const formData = new FormData(this.form);
          const mainResponse = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });

          if (!mainResponse.ok) {
            const errData = await mainResponse.json();
            console.error('メイン商品のカート追加エラー:', errData);
            this.hideLoadingOverlay();
            this.handleErrorMessage(errData.description || 'カートに追加できませんでした。');
            return;
          }

          console.log('✅ メイン商品をカートに追加しました');

          // 2. レンタル variant を決定
          let rentalVariantId = null;

          if (rental.source === 'metafield' && rental.variantId) {
            // 新方式：metafield から直接 variant_id 指定
            rentalVariantId = rental.variantId;
            console.log(`✅ レンタル連携(metafield): variant_id=${rentalVariantId}`);
          } else {
            // 旧方式フォールバック：handle から fetch、title 文字列マッチ
            const rentalResponse = await fetch('/products/' + rental.handle + '.js');
            const rentalProduct = await rentalResponse.json();

            if (!rentalProduct || !rentalProduct.variants || rentalProduct.variants.length === 0) {
              console.error('レンタル品商品が見つかりません:', rental.handle);
              this.hideLoadingOverlay();
              alert(`${rental.label}の追加に失敗しました。\n管理者にお問い合わせください。`);
              this.triggerCartUpdate();
              return;
            }

            // イベント開催日に一致するバリアントを検索
            const matchingVariant = rentalProduct.variants.find(v =>
              v.title.includes(eventDate)
            );

            if (!matchingVariant) {
              console.error(`対応する${rental.label}バリアントが見つかりません:`, eventDate);
              this.hideLoadingOverlay();
              alert(`この日程（${eventDate}）の${rental.label}は現在ご利用いただけません。\n管理者にお問い合わせください。`);
              this.triggerCartUpdate();
              return;
            }

            if (matchingVariant.available === false) {
              console.error(`${rental.label}在庫切れ:`, eventDate);
              this.hideLoadingOverlay();
              alert(`この日程（${eventDate}）の${rental.label}は在庫切れです。`);
              this.triggerCartUpdate();
              return;
            }

            rentalVariantId = matchingVariant.id;
            console.log(`✅ レンタル連携(fallback): variant_id=${rentalVariantId}`);
          }

          // 3. レンタル品をカートに追加
          const cartResponse = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{
                id: rentalVariantId,
                quantity: rentalCount,
                properties: {
                  '_関連商品': `イベント参加用${rental.label}`,
                  '_講習日程': eventDate
                }
              }]
            })
          });

          if (cartResponse.status === 422) {
            const data = await cartResponse.json();
            console.warn(`${rental.label}在庫不足:`, data);
            this.hideLoadingOverlay();
            alert(`${rental.label}の在庫が不足しています。\n${data.description || '管理者にお問い合わせください。'}`);
            this.triggerCartUpdate();
            return;
          }

          if (!cartResponse.ok) {
            const data = await cartResponse.json();
            console.error(`${rental.label}のカート追加エラー:`, data);
            this.hideLoadingOverlay();
            alert(`${rental.label}の追加に失敗しました。\n${data.description || '在庫が不足している可能性があります。'}`);
            this.triggerCartUpdate();
            return;
          }

          console.log(`✅ ${rental.label} ${rentalCount}個 (${eventDate}分) をカートに追加しました`);

          // 4. カートページへ遷移
          window.location.href = '/cart';

        } catch (error) {
          console.error('カート追加処理に失敗:', error);
          this.hideLoadingOverlay();
          alert(`カートへの追加中にエラーが発生しました。\nページを再読み込みしてもう一度お試しください。`);
        } finally {
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
        }
      }

      /**
       * カートUIを更新（カートドロワーやカウント等）
       */
      triggerCartUpdate() {
        // セクション再描画をトリガー
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        // Shopify標準のカートカウント更新
        fetch('/cart.js')
          .then(r => r.json())
          .then(cart => {
            const cartCountElements = document.querySelectorAll('.cart-count-bubble span[aria-hidden]');
            cartCountElements.forEach(el => { el.textContent = cart.item_count; });
            const cartIconBubbles = document.querySelectorAll('cart-icon-bubble');
            cartIconBubbles.forEach(el => { if(el.updateCount) el.updateCount(cart.item_count); });
          })
          .catch(() => {});
      }

      /**
       * 参加者情報のバリデーション
       * @returns {boolean} バリデーション成功: true, 失敗: false
       */
      validateEventParticipants() {
        // グローバルに公開されたバリデーション関数を呼び出し
        if (window.EventProductValidation && window.EventProductValidation[this.sectionId]) {
          const validator = window.EventProductValidation[this.sectionId];
          return validator.validateParticipantInfo();
        }

        // バリデーション関数が見つからない場合はエラーログ
        console.error(`Validation function not found for section: ${this.sectionId}`);
        return true; // 安全のため、バリデーションをスキップして続行
      }

      /**
       * エラーメッセージ表示（親クラスにない場合のフォールバック）
       */
      handleErrorMessage(message) {
        if (super.handleErrorMessage) {
          super.handleErrorMessage(message);
        } else {
          alert(message);
        }
      }
    }
  );
}
